<template>
  <div v-redirect-if-no-permission:VIEW_FINANCE_REPORTS class="fin-page">
    <finance-section-nav />
    <loading-component :value="loading" />

    <v-card>
      <div class="fin-header fin-header--sticky fr-no-print">
        <div class="fin-toolbar">
          <span class="fin-title">{{ t("title") }}</span>
          <v-spacer></v-spacer>
          <v-tooltip bottom>
            <v-btn slot="activator" icon small flat :disabled="view !== 'detailed'" @click="expandAll">
              <v-icon small>unfold_more</v-icon>
            </v-btn>
            <span>{{ c("expandAll") }}</span>
          </v-tooltip>
          <v-tooltip bottom>
            <v-btn slot="activator" icon small flat :disabled="view !== 'detailed'" @click="collapseAll">
              <v-icon small>unfold_less</v-icon>
            </v-btn>
            <span>{{ c("collapseAll") }}</span>
          </v-tooltip>
          <v-tooltip bottom>
            <v-btn slot="activator" icon small flat :disabled="!hasReport" @click="exportExcel">
              <v-icon small>grid_on</v-icon>
            </v-btn>
            <span>{{ c("exportExcel") }}</span>
          </v-tooltip>
          <v-tooltip bottom>
            <v-btn slot="activator" icon small flat @click="downloadCsv">
              <v-icon small>file_download</v-icon>
            </v-btn>
            <span>{{ c("exportCsv") }}</span>
          </v-tooltip>
          <v-tooltip bottom>
            <v-btn slot="activator" icon small flat :disabled="!hasReport" @click="exportPrint">
              <v-icon small>print</v-icon>
            </v-btn>
            <span>{{ c("print") }}</span>
          </v-tooltip>
          <v-tooltip bottom>
            <v-btn slot="activator" icon small flat color="primary" @click="loadReport">
              <v-icon small>refresh</v-icon>
            </v-btn>
            <span>{{ c("refresh") }}</span>
          </v-tooltip>
        </div>

        <div class="fin-filters">
          <v-text-field v-model="filters.date" type="date" :label="t('reportDate')" hide-details class="fin-field"></v-text-field>
          <v-select
            v-model="filters.sector"
            :items="sectorOptions"
            item-text="text"
            item-value="value"
            :label="t('sector')"
            hide-details
            class="fin-field"
            @change="onSectorChange"
          ></v-select>
          <account-picker
            v-if="filters.sector === 'custom'"
            v-model="filters.parent_id"
            :label="t('customParentLabel')"
            clearable
          />
          <v-select
            v-model="filters.subsector"
            :items="subsectorOptions"
            item-text="text"
            item-value="value"
            :label="t('subsector')"
            hide-details
            class="fin-field"
          ></v-select>
          <v-select
            v-model="filters.status"
            :items="statusOptions"
            item-text="text"
            item-value="value"
            :label="t('status')"
            hide-details
            class="fin-field"
          ></v-select>
          <v-select
            v-model="filters.balance"
            :items="balanceOptions"
            item-text="text"
            item-value="value"
            :label="t('balanceFilter')"
            hide-details
            class="fin-field"
          ></v-select>
          <template v-if="filters.balance === 'range'">
            <v-text-field v-model="filters.balance_from" type="number" :label="t('balanceFrom')" hide-details class="fin-field"></v-text-field>
            <v-text-field v-model="filters.balance_to" type="number" :label="t('balanceTo')" hide-details class="fin-field"></v-text-field>
          </template>
          <v-text-field v-model="filters.last_from" type="date" :label="t('lastFrom')" hide-details clearable class="fin-field"></v-text-field>
          <v-text-field v-model="filters.last_to" type="date" :label="t('lastTo')" hide-details clearable class="fin-field"></v-text-field>
          <v-text-field v-model="filters.days_from" type="number" min="0" :label="t('daysFrom')" hide-details class="fin-field ai-narrow"></v-text-field>
          <v-text-field v-model="filters.days_to" type="number" min="0" :label="t('daysTo')" hide-details class="fin-field ai-narrow"></v-text-field>
          <v-text-field v-model="filters.not_moved_days" type="number" min="0" :label="t('notMovedDays')" hide-details class="fin-field ai-narrow"></v-text-field>
          <v-select
            v-model="filters.party_type"
            :items="partyTypeOptions"
            item-text="text"
            item-value="value"
            :label="t('partyType')"
            hide-details
            class="fin-field"
          ></v-select>
          <v-text-field
            v-model="filters.party_search"
            :label="t('partySearch')"
            hide-details
            clearable
            class="fin-field"
            @keyup.enter="loadReport"
          ></v-text-field>
          <v-select
            v-model="filters.province"
            :items="provinceOptions"
            item-text="text"
            item-value="value"
            :label="t('province')"
            hide-details
            class="fin-field"
          ></v-select>
          <v-select
            v-model="filters.party_status"
            :items="partyStatusOptions"
            item-text="text"
            item-value="value"
            :label="t('partyStatus')"
            hide-details
            class="fin-field"
          ></v-select>
          <v-btn small flat @click="showAdvanced = !showAdvanced">
            <v-icon small class="me-1">tune</v-icon>{{ t("advanced") }}
          </v-btn>
          <v-btn small color="primary" @click="loadReport">{{ c("apply") }}</v-btn>
        </div>

        <div v-if="showAdvanced" class="fin-filters">
          <v-text-field v-model="filters.year_start" type="date" :label="t('yearStart')" hide-details clearable class="fin-field"></v-text-field>
          <v-text-field v-model="filters.active_days" type="number" min="1" :label="t('activeDays')" hide-details class="fin-field ai-narrow"></v-text-field>
          <v-text-field v-model="filters.semi_days" type="number" min="1" :label="t('semiDays')" hide-details class="fin-field ai-narrow"></v-text-field>
          <v-text-field v-model="filters.dormant_days" type="number" min="1" :label="t('dormantDays')" hide-details class="fin-field ai-narrow"></v-text-field>
          <v-text-field v-model="filters.large_balance" type="number" min="0" :label="t('largeBalance')" hide-details clearable class="fin-field"></v-text-field>
          <span class="ai-hint">{{ t("thresholdsHint") }}</span>
        </div>
      </div>

      <div v-for="(warning, index) in warnings" :key="'w' + index" class="fin-alert-error">
        <v-icon small color="#c62828">warning</v-icon>
        {{ warning.text }}
      </div>

      <div v-if="hasReport" class="ai-header-line">
        <span><b>{{ report.header.company_name }}</b></span>
        <span>{{ report.header.title }}</span>
        <span>{{ t("reportDate") }}: {{ report.date }}</span>
        <span>{{ c("currency") }}: {{ report.header.currency_label }}</span>
        <span>{{ t("headerSector") }}: {{ report.header.sector_label || t("allSectors") }}</span>
        <span v-if="report.header.subsector_label">{{ t("headerSubsector") }}: {{ report.header.subsector_label }}</span>
        <span>{{ t("headerAccounts") }}: {{ report.header.accounts_count }}</span>
        <span class="ai-muted">{{ c("issuedAt") }}: {{ formatStamp(report.header.issued_at) }}</span>
      </div>

      <div v-if="hasReport" class="fin-summary">
        <div class="fin-stat">
          <span class="fin-stat-label">{{ t("totalAccounts") }}</span>
          <span class="fin-stat-value">{{ total.accounts }}</span>
        </div>
        <div class="fin-stat">
          <span class="fin-stat-label">{{ t("active") }}</span>
          <span class="fin-stat-value">{{ total.active }}</span>
        </div>
        <div class="fin-stat">
          <span class="fin-stat-label">{{ t("semiActive") }}</span>
          <span class="fin-stat-value">{{ total.semi_active }}</span>
        </div>
        <div class="fin-stat">
          <span class="fin-stat-label">{{ t("dormant") }}</span>
          <span class="fin-stat-value">{{ total.dormant }}</span>
        </div>
        <div class="fin-stat">
          <span class="fin-stat-label">{{ t("veryDormant") }}</span>
          <span class="fin-stat-value">{{ total.very_dormant }}</span>
        </div>
        <div class="fin-stat">
          <span class="fin-stat-label">{{ t("unused") }}</span>
          <span class="fin-stat-value">{{ total.unused }}</span>
        </div>
        <div class="fin-stat">
          <span class="fin-stat-label">{{ t("closed") }}</span>
          <span class="fin-stat-value">{{ total.closed }}</span>
        </div>
        <div class="fin-stat">
          <span class="fin-stat-label">{{ t("debitBalances") }}</span>
          <span class="fin-stat-value fin-num">{{ formatMoney(total.debit_balances) }}</span>
        </div>
        <div class="fin-stat">
          <span class="fin-stat-label">{{ t("creditBalances") }}</span>
          <span class="fin-stat-value fin-num">{{ formatMoney(total.credit_balances) }}</span>
        </div>
        <div class="fin-stat">
          <span class="fin-stat-label">{{ t("net") }}</span>
          <span class="fin-stat-value fin-num">{{ formatMoney(total.net) }}</span>
        </div>
        <v-chip v-if="total.abnormal" small color="red" text-color="white">{{ t("abnormal") }}: {{ total.abnormal }}</v-chip>
        <v-chip v-if="total.needs_review" small color="orange" text-color="white">{{ t("needsReview") }}: {{ total.needs_review }}</v-chip>
        <v-chip v-if="total.closable" small color="blue-grey" text-color="white">{{ t("closable") }}: {{ total.closable }}</v-chip>
      </div>

      <div v-if="hasReport" class="ai-views fr-no-print">
        <v-btn-toggle v-model="view" mandatory>
          <v-btn flat small value="detailed">{{ t("viewDetailed") }} ({{ report.rows.length }})</v-btn>
          <v-btn flat small value="summary">{{ t("viewSummary") }}</v-btn>
          <v-btn flat small value="alerts">{{ t("viewAlerts") }} ({{ report.alerts.length }})</v-btn>
          <v-btn flat small value="deleted">{{ t("viewDeleted") }} ({{ report.deleted_party_rows.length }})</v-btn>
        </v-btn-toggle>
        <v-spacer></v-spacer>
        <template v-if="view === 'detailed'">
          <span class="ai-muted">{{ c("perPage") }}</span>
          <v-select v-model="perPage" :items="perPageOptions" item-text="text" item-value="value" hide-details class="ai-per-page"></v-select>
        </template>
      </div>

      <!-- Detailed table, grouped by sector -->
      <div v-if="hasReport && view === 'detailed'" class="ai-table-wrap fin-table-wrap">
        <table class="ai-table fin-table">
          <thead>
            <tr>
              <th></th>
              <th>{{ t("colIndex") }}</th>
              <th>{{ t("colCode") }}</th>
              <th>{{ t("colName") }}</th>
              <th>{{ t("colParty") }}</th>
              <th class="fin-num-head">{{ t("colOpening") }}</th>
              <th class="fin-num-head">{{ t("colBalance") }}</th>
              <th>{{ t("colBalanceType") }}</th>
              <th>{{ t("colLastDate") }}</th>
              <th class="fin-num-head">{{ t("colDays") }}</th>
              <th class="fin-num-head">{{ t("colMovements") }}</th>
              <th>{{ t("colStatus") }}</th>
              <th>{{ t("colAction") }}</th>
              <th>{{ t("colSubsector") }}</th>
              <th>{{ t("colProvince") }}</th>
            </tr>
          </thead>
          <tbody>
            <template v-for="item in pageItems">
              <tr v-if="item.group" :key="'g-' + item.key" class="ai-group-row" @click="toggleGroup(item.key)">
                <td colspan="15">
                  <v-icon small>{{ isGroupOpen(item.key) ? "remove" : "add" }}</v-icon>
                  {{ item.label }} ({{ item.count }})
                </td>
              </tr>
              <template v-else>
                <tr :key="'r-' + item.row.account_id" :class="rowClasses(item.row)">
                  <td class="ai-toggle-cell">
                    <v-btn icon small class="ai-toggle" @click="toggleRow(item.row.account_id)">
                      <v-icon small>{{ openRows[item.row.account_id] ? "expand_less" : "expand_more" }}</v-icon>
                    </v-btn>
                  </td>
                  <td>{{ item.index }}</td>
                  <td class="fin-code">{{ item.row.account_code }}</td>
                  <td>
                    <span class="fin-ellipsis" :title="item.row.account_name">{{ item.row.account_name }}</span>
                    <span v-if="item.row.age_flag" class="ai-flag">{{ ageFlagLabel(item.row.age_flag) }}</span>
                  </td>
                  <td :class="{ 'ai-missing': item.row.party_name_missing }">
                    {{ item.row.party_name || "" }}
                    <span v-if="item.row.party_type" class="ai-muted">({{ partyTypeLabel(item.row.party_type) }})</span>
                  </td>
                  <td class="fin-num">{{ formatMoney(item.row.opening_balance) }}</td>
                  <td class="fin-num">{{ formatMoney(item.row.current_balance) }}</td>
                  <td>
                    <span :class="'ai-side-' + item.row.balance_type">{{ sideLabel(item.row.balance_type) }}</span>
                  </td>
                  <td class="fin-code">{{ item.row.last_movement_date || "—" }}</td>
                  <td class="fin-num">{{ item.row.days_since_last_movement === null ? "—" : item.row.days_since_last_movement }}</td>
                  <td class="fin-num">{{ item.row.total_movements }}</td>
                  <td>
                    <span :class="'ai-status ai-status-' + item.row.account_status">{{ statusLabel(item.row.account_status) }}</span>
                    <span v-if="item.row.is_zero_balance" class="ai-flag">{{ statusLabel("zero_balance") }}</span>
                  </td>
                  <td :class="'ai-action-' + item.row.recommended_action">
                    {{ actionLabel(item.row.recommended_action) }}
                    <div v-if="item.row.is_abnormal_balance" class="ai-abnormal">{{ item.row.abnormal_text }}</div>
                  </td>
                  <td>{{ item.row.subsector_label || "" }}</td>
                  <td>{{ item.row.province || "" }}</td>
                </tr>
                <tr v-if="openRows[item.row.account_id]" :key="'d-' + item.row.account_id" class="ai-detail-row">
                  <td></td>
                  <td colspan="14">
                    <div class="ai-detail-grid">
                      <div v-for="pair in detailPairs(item.row)" :key="pair[0]" class="ai-detail-item">
                        <span class="ai-detail-label">{{ pair[0] }}</span>
                        <span class="ai-detail-value">{{ pair[1] }}</span>
                      </div>
                    </div>
                  </td>
                </tr>
              </template>
            </template>
            <tr v-if="!report.rows.length">
              <td colspan="15" class="fin-empty">{{ c("noData") }}</td>
            </tr>
          </tbody>
        </table>
        <div v-if="pageCount > 1" class="ai-pager">
          <v-pagination v-model="page" :length="pageCount" :total-visible="9"></v-pagination>
        </div>
      </div>

      <!-- Summary per sector -->
      <div v-if="hasReport && view === 'summary'" class="ai-table-wrap fin-table-wrap">
        <table class="ai-table fin-table">
          <thead>
            <tr>
              <th v-for="column in summaryColumns" :key="column.key" :class="{ 'fin-num-head': column.type !== 'text' }">
                {{ column.label }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="line in summaryRows" :key="line.key" :class="{ 'ai-total-row': line.key === 'total' }">
              <td v-for="column in summaryColumns" :key="column.key" :class="{ 'fin-num': column.type !== 'text' }">
                {{ frCellText(column, line[column.key]) }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Alerts -->
      <div v-if="hasReport && view === 'alerts'" class="ai-table-wrap fin-table-wrap">
        <table class="ai-table fin-table">
          <thead>
            <tr>
              <th v-for="column in alertColumns" :key="column.key">{{ column.label }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(alert, index) in alertRows" :key="'a' + index">
              <td v-for="column in alertColumns" :key="column.key" :class="{ 'fin-code': column.type === 'code' }">
                {{ frCellText(column, alert[column.key]) }}
              </td>
            </tr>
            <tr v-if="!alertRows.length">
              <td :colspan="alertColumns.length" class="fin-empty">{{ t("noAlerts") }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Accounts of deleted parties (her p40: a separate list) -->
      <div v-if="hasReport && view === 'deleted'" class="ai-table-wrap fin-table-wrap">
        <p class="ai-note">{{ t("deletedNote") }}</p>
        <table class="ai-table fin-table">
          <thead>
            <tr>
              <th v-for="column in printColumns" :key="column.key">{{ column.label }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, index) in deletedRows" :key="'x' + index">
              <td v-for="column in printColumns" :key="column.key" :class="{ 'fin-num': frIsNumericColumn(column), 'fin-code': column.type === 'code' }">
                {{ frCellText(column, row[column.key]) }}
              </td>
            </tr>
            <tr v-if="!deletedRows.length">
              <td :colspan="printColumns.length" class="fin-empty">{{ t("noDeleted") }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-if="hasReport && report.notes && report.notes.length" class="ai-notes">
        <p v-for="(note, index) in report.notes" :key="'n' + index" class="ai-note">{{ note }}</p>
      </div>
    </v-card>
  </div>
</template>

<script>
/*
 * Report 2.7 of the accountant's document 2026-10-03 (accounts-inventory).
 * API: this.api.ACTIONS.ACCOUNTING_ACCOUNTS_INVENTORY (GET, format=json|csv).
 * Strings: finance.reports2.accountsInventory.* (src/locales/finance-reports/accounts-inventory.{ar,en}.js)
 * and finance.reports2.common.*. Exports: mixin financeReportExport
 * (Excel with 4 sheets: header, detailed, summary, alerts).
 *
 * Money in the payload is STORED (old SYP); formatMoney / the mixin divide
 * by 100. Money filters typed here are converted back with toStoredAmount.
 */
import FinanceSectionNav from "@/components/finance/FinanceSectionNav";
import LoadingComponent from "@/components/LoadingComponent";
import AccountPicker from "@/components/finance/AccountPicker";
import financeReportExport from "@/mixins/financeReportExport";
import { formatFinanceDate } from "@/mixins/financeDate";
import { companyToday } from "@/util/companyTime";

const STATUS_KEYS =["active", "semi_active", "dormant", "very_dormant", "unused", "closed", "zero_balance"];
const ACTION_KEYS = ["correct_balance", "close", "urgent_review", "alert", "normal", "none"];
// Same keys as AccountsInventoryService::PARTY_TYPES (employees: payroll wage leaves under 21311).
const PARTY_TYPE_KEYS = ["captain", "center", "business", "owner", "employee"];
const PARTY_STATUS_KEYS = ["active", "suspended", "deleted"];

// Final-version doc 2026-10-05: the company (Damascus) day on the
// server-synced clock, not the PC calendar.
function todayString() {
  return companyToday();
}

export default {
  name: "AccountsInventory",
  title() {
    return this.$t("finance.reports2.accountsInventory.pageTitle");
  },
  components: { FinanceSectionNav, LoadingComponent, AccountPicker },
  mixins: [financeReportExport],
  data() {
    return {
      loading: false,
      report: null,
      view: "detailed",
      showAdvanced: false,
      page: 1,
      perPage: 50,
      closedGroups: {},
      openRows: {},
      filters: {
        date: todayString(),
        year_start: null,
        sector: null,
        parent_id: null,
        subsector: null,
        status: null,
        balance: null,
        balance_from: null,
        balance_to: null,
        last_from: null,
        last_to: null,
        days_from: null,
        days_to: null,
        not_moved_days: null,
        party_type: null,
        party_search: null,
        province: null,
        party_status: null,
        active_days: 30,
        semi_days: 60,
        dormant_days: 90,
        large_balance: null,
      },
    };
  },
  computed: {
    hasReport() {
      return !!(this.report && this.report.header && this.report.summary);
    },
    options() {
      return (this.report && this.report.options) || {};
    },
    warnings() {
      return (this.report && this.report.warnings) || [];
    },
    total() {
      return (this.hasReport && this.report.summary.total) || {};
    },
    sectorOrder() {
      const order = {};
      (this.options.sectors || []).forEach((sector, index) => {
        order[sector.key] = index;
      });
      return order;
    },
    sectorOptions() {
      const items = [{ text: this.t("allSectors"), value: null }];
      (this.options.sectors || []).forEach((sector) => {
        items.push({ text: sector.label + (sector.codes.length ? " (" + sector.codes.join("، ") + ")" : ""), value: sector.key });
      });
      items.push({ text: this.t("customParent"), value: "custom" });
      return items;
    },
    subsectorOptions() {
      const items = [{ text: this.t("allSubsectors"), value: null }];
      (this.options.subsectors || []).forEach((option) => {
        if (!this.filters.sector || this.filters.sector === "custom" || option.sector === this.filters.sector) {
          items.push({ text: option.label, value: option.key });
        }
      });
      return items;
    },
    statusOptions() {
      return [{ text: this.t("allStatuses"), value: null }].concat(
        STATUS_KEYS.map((key) => ({ text: this.statusLabel(key), value: key }))
      );
    },
    balanceOptions() {
      return [
        { text: this.t("balanceAll"), value: null },
        { text: this.t("balancePositive"), value: "positive" },
        { text: this.t("balanceNegative"), value: "negative" },
        { text: this.t("balanceZero"), value: "zero" },
        { text: this.t("balanceAbnormal"), value: "abnormal" },
        { text: this.t("balanceRange"), value: "range" },
      ];
    },
    partyTypeOptions() {
      return [{ text: this.t("allPartyTypes"), value: null }]
        .concat(PARTY_TYPE_KEYS.map((key) => ({ text: this.partyTypeLabel(key), value: key })))
        .concat([{ text: this.t("noParty"), value: "none" }]);
    },
    partyStatusOptions() {
      return [{ text: this.t("allPartyStatuses"), value: null }].concat(
        PARTY_STATUS_KEYS.map((key) => ({ text: this.partyStatusLabel(key), value: key }))
      );
    },
    provinceOptions() {
      return [{ text: this.t("allProvinces"), value: null }].concat(
        (this.options.provinces || []).map((name) => ({ text: name, value: name }))
      );
    },
    perPageOptions() {
      return [
        { text: "50", value: 50 },
        { text: "100", value: 100 },
        { text: "500", value: 500 },
        { text: this.c("showAll"), value: 0 },
      ];
    },
    /** Detail rows sorted by her sector order, chart order inside a sector. */
    sortedRows() {
      if (!this.hasReport) {
        return [];
      }
      const order = this.sectorOrder;
      return this.report.rows
        .map((row, index) => ({ row, index }))
        .sort((a, b) => {
          const sa = order[a.row.sector] === undefined ? 999 : order[a.row.sector];
          const sb = order[b.row.sector] === undefined ? 999 : order[b.row.sector];
          return sa !== sb ? sa - sb : a.index - b.index;
        })
        .map((entry) => entry.row);
    },
    groupCounts() {
      const counts = {};
      this.sortedRows.forEach((row) => {
        counts[row.sector] = (counts[row.sector] || 0) + 1;
      });
      return counts;
    },
    /**
     * Sector header + rows, in order. A collapsed sector keeps its header so
     * it can be reopened; the row number (#) counts every row of the report.
     */
    allItems() {
      const items = [];
      let lastSector = null;
      this.sortedRows.forEach((row, index) => {
        if (row.sector !== lastSector) {
          items.push({ group: true, key: row.sector, label: row.sector_label, count: this.groupCounts[row.sector] || 0 });
          lastSector = row.sector;
        }
        if (!this.closedGroups[row.sector]) {
          items.push({ group: false, row: row, index: index + 1 });
        }
      });
      return items;
    },
    pageCount() {
      if (!this.perPage) {
        return 1;
      }
      return Math.max(1, Math.ceil(this.allItems.length / this.perPage));
    },
    /** One page of items; a page that starts inside a sector repeats its header. */
    pageItems() {
      if (!this.perPage) {
        return this.allItems;
      }
      const start = (this.page - 1) * this.perPage;
      const items = this.allItems.slice(start, start + this.perPage);
      if (items.length && !items[0].group) {
        const row = items[0].row;
        items.unshift({ group: true, key: row.sector, label: row.sector_label, count: this.groupCounts[row.sector] || 0 });
      }
      return items;
    },
    summaryColumns() {
      return [
        { key: "label", label: this.t("colSector"), type: "text", width: 22 },
        { key: "accounts", label: this.t("totalAccounts"), type: "count" },
        { key: "active", label: this.t("active"), type: "count" },
        { key: "semi_active", label: this.t("semiActive"), type: "count" },
        { key: "dormant", label: this.t("dormant"), type: "count" },
        { key: "very_dormant", label: this.t("veryDormant"), type: "count" },
        { key: "unused", label: this.t("unused"), type: "count" },
        { key: "closed", label: this.t("closed"), type: "count" },
        { key: "zero_balance", label: this.t("zeroBalance"), type: "count" },
        { key: "debit_balances", label: this.t("debitBalances"), type: "money" },
        { key: "credit_balances", label: this.t("creditBalances"), type: "money" },
        { key: "net", label: this.t("net"), type: "money" },
        { key: "abnormal", label: this.t("abnormal"), type: "count" },
        { key: "needs_review", label: this.t("needsReview"), type: "count" },
        { key: "closable", label: this.t("closable"), type: "count" },
      ];
    },
    summaryRows() {
      if (!this.hasReport) {
        return [];
      }
      const total = Object.assign({}, this.report.summary.total, { label: this.c("total") });
      return this.report.summary.sectors.concat([total]);
    },
    alertColumns() {
      return [
        { key: "alert", label: this.t("colAlert"), type: "text", width: 30 },
        { key: "condition", label: this.t("colCondition"), type: "text", width: 26 },
        { key: "action", label: this.t("colAlertAction"), type: "text", width: 16 },
        { key: "account_code", label: this.t("colCode"), type: "code", width: 18 },
        { key: "account_name", label: this.t("colName"), type: "text", width: 30 },
        { key: "party_name", label: this.t("colParty"), type: "text", width: 24 },
        { key: "sector_label", label: this.t("colSector"), type: "text", width: 16 },
        { key: "value_text", label: this.t("colValue"), type: "text", width: 16 },
      ];
    },
    alertRows() {
      if (!this.hasReport) {
        return [];
      }
      return this.report.alerts.map((alert) =>
        Object.assign({}, alert, {
          value_text:
            alert.value === null || alert.value === undefined
              ? ""
              : alert.value_is_money
              ? this.formatMoney(alert.value)
              : String(alert.value),
        })
      );
    },
    /** Compact column set (her p37 example layout): screen "deleted" list and print. */
    printColumns() {
      return [
        { key: "index", label: this.t("colIndex"), type: "count", width: 6 },
        { key: "account_code", label: this.t("colCode"), type: "code", width: 18 },
        { key: "account_name", label: this.t("colName"), type: "text", width: 28 },
        { key: "party_name", label: this.t("colParty"), type: "text", width: 22 },
        { key: "current_balance", label: this.t("colBalance"), type: "money" },
        { key: "balance_type_text", label: this.t("colBalanceType"), type: "text", width: 10 },
        { key: "last_movement_date", label: this.t("colLastDate"), type: "code", width: 12 },
        { key: "days_since_last_movement", label: this.t("colDays"), type: "count", width: 8 },
        { key: "status_text", label: this.t("colStatus"), type: "text", width: 12 },
        { key: "action_text", label: this.t("colAction"), type: "text", width: 16 },
      ];
    },
    deletedRows() {
      if (!this.hasReport) {
        return [];
      }
      return this.report.deleted_party_rows.map((row, index) => this.exportRow(row, index + 1));
    },
  },
  watch: {
    perPage() {
      this.page = 1;
    },
  },
  mounted() {
    this.loadReport();
  },
  methods: {
    t(key) {
      return this.$t("finance.reports2.accountsInventory." + key);
    },
    c(key) {
      return this.$t("finance.reports2.common." + key);
    },
    statusLabel(key) {
      return key ? this.t("statusLabels." + key) : "";
    },
    actionLabel(key) {
      return key && ACTION_KEYS.indexOf(key) !== -1 ? this.t("actionLabels." + key) : "";
    },
    partyTypeLabel(key) {
      return key ? this.t("partyTypeLabels." + key) : "";
    },
    partyStatusLabel(key) {
      return key ? this.t("partyStatusLabels." + key) : "";
    },
    sideLabel(key) {
      return key ? this.t("sideLabels." + key) : "";
    },
    ageFlagLabel(key) {
      return key ? this.t("ageFlags." + key) : "";
    },
    // Final-version doc 2026-10-05: server timestamps in company (Damascus)
    // time even if a zoned value arrives; seconds kept as before.
    formatStamp(value) {
      return value === null || value === undefined || value === "" ? "—" : formatFinanceDate(value, true);
    },
    yesNo(flag) {
      return flag ? this.t("yes") : this.t("no");
    },
    onSectorChange() {
      this.filters.subsector = null;
      if (this.filters.sector !== "custom") {
        this.filters.parent_id = null;
      }
    },
    hasValue(value) {
      return value !== null && value !== undefined && value !== "";
    },
    buildParams() {
      const f = this.filters;
      const params = {};
      ["date", "year_start", "subsector", "status", "last_from", "last_to", "party_type", "party_search", "province", "party_status"].forEach(
        (key) => {
          if (this.hasValue(f[key])) {
            params[key] = f[key];
          }
        }
      );
      if (this.hasValue(f.sector)) {
        params.sector = f.sector;
        if (f.sector === "custom" && this.hasValue(f.parent_id)) {
          params.parent_id = f.parent_id;
        }
      }
      ["days_from", "days_to", "not_moved_days", "active_days", "semi_days", "dormant_days"].forEach((key) => {
        if (this.hasValue(f[key])) {
          params[key] = parseInt(f[key], 10);
        }
      });
      if (this.hasValue(f.balance)) {
        params.balance = f.balance;
        if (f.balance === "range") {
          if (this.hasValue(f.balance_from)) params.balance_from = this.toStoredAmount(f.balance_from);
          if (this.hasValue(f.balance_to)) params.balance_to = this.toStoredAmount(f.balance_to);
        }
      }
      if (this.hasValue(f.large_balance)) {
        params.large_balance = this.toStoredAmount(f.large_balance);
      }
      return params;
    },
    loadReport() {
      if (this.filters.balance === "range" && !this.hasValue(this.filters.balance_from) && !this.hasValue(this.filters.balance_to)) {
        window.getApp.$emit("SHOW_TOAST", this.t("rangeNeedsBounds"), "red");
        return;
      }
      this.loading = true;
      this.axios
        .get(this.api.ACTIONS.ACCOUNTING_ACCOUNTS_INVENTORY, { params: this.buildParams() })
        .then((response) => {
          this.report = (response.data && response.data.data) || null;
          this.page = 1;
          this.openRows = {};
        })
        .catch((error) => {
          window.getApp.$emit("SHOW_TOAST", this.frErrorMessage(error, this.c("failedLoad")), "red");
        })
        .finally(() => {
          this.loading = false;
        });
    },
    downloadCsv() {
      this.frDownloadCsv(this.api.ACTIONS.ACCOUNTING_ACCOUNTS_INVENTORY, this.buildParams(), "accounts-inventory");
    },
    isGroupOpen(key) {
      return !this.closedGroups[key];
    },
    toggleGroup(key) {
      const next = Object.assign({}, this.closedGroups);
      if (next[key]) {
        delete next[key];
      } else {
        next[key] = true;
      }
      this.closedGroups = next;
      if (this.page > this.pageCount) {
        this.page = this.pageCount;
      }
    },
    toggleRow(id) {
      const next = Object.assign({}, this.openRows);
      if (next[id]) {
        delete next[id];
      } else {
        next[id] = true;
      }
      this.openRows = next;
    },
    expandAll() {
      this.closedGroups = {};
    },
    collapseAll() {
      const next = {};
      Object.keys(this.groupCounts).forEach((key) => {
        next[key] = true;
      });
      this.closedGroups = next;
      this.page = 1;
    },
    rowClasses(row) {
      return {
        "ai-row-abnormal": row.is_abnormal_balance,
        "ai-row-review": !row.is_abnormal_balance && row.needs_review,
        "ai-row-closed": row.account_status === "closed",
      };
    },
    detailPairs(row) {
      const money = (value) => (value === null || value === undefined ? "—" : this.formatMoney(value));
      const text = (value) => (value === null || value === undefined || value === "" ? "—" : String(value));
      return [
        [this.t("colLevel"), text(row.level)],
        [this.t("colParentCode"), text(row.parent_code)],
        [this.t("colParentName"), text(row.parent_name)],
        [this.t("colType"), text(row.account_type_label)],
        [this.t("colNature"), this.sideLabel(row.normal_balance)],
        [this.t("colIsActive"), this.yesNo(row.is_active)],
        [this.t("colAccountCreatedAt"), this.formatStamp(row.account_created_at)],
        [this.t("colPartyId"), text(row.party_id)],
        [this.t("colPartyPhone"), text(row.party_phone)],
        [this.t("colPartyStatus"), row.party_status ? this.partyStatusLabel(row.party_status) : "—"],
        [this.t("colPartyCreatedAt"), this.formatStamp(row.party_created_at)],
        [this.t("colLastType"), row.last_movement_type ? this.sideLabel(row.last_movement_type) : "—"],
        [this.t("colLastAmount"), money(row.last_movement_amount)],
        [this.t("colLastJournal"), text(row.last_journal_id)],
        [this.t("colLastReference"), text(row.last_reference)],
        [this.t("colTotalDebit"), money(row.total_debit)],
        [this.t("colTotalCredit"), money(row.total_credit)],
        [this.t("colBalanceAge"), text(row.balance_age_days)],
        [this.t("colDormant"), this.yesNo(row.is_dormant)],
        [this.t("colDormancyReason"), text(row.dormancy_reason)],
        [this.t("colNeedsReview"), this.yesNo(row.needs_review)],
        [this.t("colSector"), text(row.sector_label)],
        [this.t("colGroup"), text(row.account_group)],
      ];
    },

    // ---------------------------------------------------------------
    // Exports
    // ---------------------------------------------------------------

    /** A payload row flattened for the export columns (labels resolved). */
    exportRow(row, index) {
      return Object.assign({}, row, {
        index: index,
        account_type_text: row.account_type_label,
        normal_balance_text: this.sideLabel(row.normal_balance),
        is_active_text: this.yesNo(row.is_active),
        is_leaf_text: this.yesNo(row.is_leaf),
        party_type_text: this.partyTypeLabel(row.party_type),
        party_status_text: this.partyStatusLabel(row.party_status),
        last_type_text: this.sideLabel(row.last_movement_type),
        balance_type_text: this.sideLabel(row.balance_type),
        abnormal_text_value: row.is_abnormal_balance ? row.abnormal_text || this.t("yes") : this.t("no"),
        status_text: this.statusLabel(row.account_status),
        zero_text: this.yesNo(row.is_zero_balance),
        dormant_text: this.yesNo(row.is_dormant),
        review_text: this.yesNo(row.needs_review),
        action_text: this.actionLabel(row.recommended_action),
        age_text: this.ageFlagLabel(row.age_flag),
        _highlight: row.is_abnormal_balance ? "error" : row.needs_review ? "warn" : undefined,
      });
    },
    detailedColumns() {
      return [
        { key: "index", label: this.t("colIndex"), type: "count", width: 6 },
        { key: "account_code", label: this.t("colCode"), type: "code", width: 18 },
        { key: "account_name", label: this.t("colName"), type: "text", width: 30 },
        { key: "level", label: this.t("colLevel"), type: "count", width: 7 },
        { key: "parent_code", label: this.t("colParentCode"), type: "code", width: 14 },
        { key: "parent_name", label: this.t("colParentName"), type: "text", width: 24 },
        { key: "account_type_text", label: this.t("colType"), type: "text", width: 10 },
        { key: "normal_balance_text", label: this.t("colNature"), type: "text", width: 9 },
        { key: "is_active_text", label: this.t("colIsActive"), type: "text", width: 7 },
        { key: "is_leaf_text", label: this.t("colIsLeaf"), type: "text", width: 7 },
        { key: "party_id", label: this.t("colPartyId"), type: "code", width: 9 },
        { key: "party_name", label: this.t("colParty"), type: "text", width: 24 },
        { key: "party_type_text", label: this.t("colPartyType"), type: "text", width: 11 },
        { key: "party_phone", label: this.t("colPartyPhone"), type: "code", width: 14 },
        { key: "party_status_text", label: this.t("colPartyStatus"), type: "text", width: 9 },
        { key: "party_created_at", label: this.t("colPartyCreatedAt"), type: "date", width: 19 },
        { key: "last_movement_date", label: this.t("colLastDate"), type: "date", width: 12 },
        { key: "last_type_text", label: this.t("colLastType"), type: "text", width: 9 },
        { key: "last_movement_amount", label: this.t("colLastAmount"), type: "money" },
        { key: "last_journal_id", label: this.t("colLastJournal"), type: "code", width: 10 },
        { key: "last_reference", label: this.t("colLastReference"), type: "code", width: 18 },
        { key: "days_since_last_movement", label: this.t("colDays"), type: "count", width: 9 },
        { key: "total_movements", label: this.t("colMovements"), type: "count", width: 9 },
        { key: "total_debit", label: this.t("colTotalDebit"), type: "money" },
        { key: "total_credit", label: this.t("colTotalCredit"), type: "money" },
        { key: "opening_balance", label: this.t("colOpening"), type: "money" },
        { key: "current_balance", label: this.t("colBalance"), type: "money" },
        { key: "balance_type_text", label: this.t("colBalanceType"), type: "text", width: 9 },
        { key: "balance_age_days", label: this.t("colBalanceAge"), type: "count", width: 9 },
        { key: "abnormal_text_value", label: this.t("colAbnormal"), type: "text", width: 18 },
        { key: "status_text", label: this.t("colStatus"), type: "text", width: 11 },
        { key: "zero_text", label: this.t("colZeroBalance"), type: "text", width: 8 },
        { key: "dormant_text", label: this.t("colDormant"), type: "text", width: 7 },
        { key: "dormancy_reason", label: this.t("colDormancyReason"), type: "text", width: 22 },
        { key: "review_text", label: this.t("colNeedsReview"), type: "text", width: 9 },
        { key: "action_text", label: this.t("colAction"), type: "text", width: 15 },
        { key: "age_text", label: this.t("colAgeFlag"), type: "text", width: 8 },
        { key: "sector_label", label: this.t("colSector"), type: "text", width: 14 },
        { key: "subsector_label", label: this.t("colSubsector"), type: "text", width: 24 },
        { key: "province", label: this.t("colProvince"), type: "text", width: 12 },
        { key: "account_group", label: this.t("colGroup"), type: "text", width: 26 },
      ];
    },
    headerSheetRows() {
      const r = this.report;
      const h = r.header;
      const th = r.thresholds || {};
      const rows = [
        { field: this.t("headerSector"), value: h.sector_label || this.t("allSectors") },
        { field: this.t("headerSubsector"), value: h.subsector_label || this.t("allSubsectors") },
        { field: this.t("headerAccounts"), value: String(h.accounts_count) },
        { field: this.t("headerYearStart"), value: r.year_start },
        {
          field: this.t("headerThresholds"),
          value: this.t("active") + " ≤ " + th.active_days + " / " + this.t("semiActive") + " ≤ " + th.semi_days + " / " + this.t("dormant") + " ≤ " + th.dormant_days,
        },
      ];
      if (th.large_balance !== null && th.large_balance !== undefined) {
        rows.push({ field: this.t("largeBalance"), value: this.formatMoney(th.large_balance) });
      }
      this.summaryColumns.slice(1).forEach((column) => {
        rows.push({ field: column.label, value: this.frCellText(column, r.summary.total[column.key]) });
      });
      (r.warnings || []).forEach((warning) => {
        rows.push({ field: this.t("headerWarnings"), value: warning.text, _highlight: "warn" });
      });
      (r.notes || []).forEach((note) => {
        rows.push({ field: this.t("headerNotes"), value: note });
      });
      return rows;
    },
    exportExcel() {
      if (!this.hasReport) {
        return;
      }
      const textColumns = [
        { key: "field", label: this.t("field"), type: "text", width: 30 },
        { key: "value", label: this.t("value"), type: "text", width: 70 },
      ];
      const detailed = this.sortedRows.map((row, index) => this.exportRow(row, index + 1));
      if (this.report.deleted_party_rows.length) {
        detailed.push({ index: "", account_code: "", account_name: this.t("viewDeleted"), _kind: "section" });
        this.report.deleted_party_rows.forEach((row, index) => {
          detailed.push(this.exportRow(row, index + 1));
        });
      }
      const summaryRows = this.summaryRows.map((line) => Object.assign({}, line, line.key === "total" ? { _kind: "total" } : {}));
      this.frExportXlsx({
        fileName: "accounts-inventory",
        header: this.report.header,
        sheets: [
          { name: this.t("sheetHeader"), columns: textColumns, rows: this.headerSheetRows() },
          { name: this.t("sheetDetailed"), columns: this.detailedColumns(), rows: detailed },
          { name: this.t("sheetSummary"), columns: this.summaryColumns, rows: summaryRows },
          { name: this.t("sheetAlerts"), columns: this.alertColumns, rows: this.alertRows },
        ],
      });
    },
    exportPrint() {
      if (!this.hasReport) {
        return;
      }
      const h = this.report.header;
      const intro = [
        this.t("headerSector") + ": " + (h.sector_label || this.t("allSectors")),
        this.t("headerSubsector") + ": " + (h.subsector_label || this.t("allSubsectors")),
        this.t("headerAccounts") + ": " + h.accounts_count,
      ];
      const summaryRows = this.summaryRows.map((line) => Object.assign({}, line, line.key === "total" ? { _kind: "total" } : {}));
      const detailed = this.sortedRows.map((row, index) => this.exportRow(row, index + 1));
      const sections = [
        { title: this.t("printSummary"), columns: this.summaryColumns, rows: summaryRows, notes: intro },
        { title: this.t("printDetailed"), columns: this.printColumns, rows: detailed },
      ];
      if (this.deletedRows.length) {
        sections.push({ title: this.t("viewDeleted"), columns: this.printColumns, rows: this.deletedRows, notes: [this.t("deletedNote")] });
      }
      if (this.alertRows.length) {
        sections.push({ title: this.t("printAlerts"), columns: this.alertColumns.filter((column) => column.key !== "sector_label"), rows: this.alertRows });
      }
      this.frPrint({
        header: h,
        sections: sections,
        notes: (this.report.warnings || []).map((warning) => warning.text).concat(this.report.notes || []),
        orientation: "portrait",
      });
    },
  },
};
</script>

<style scoped>
.ai-header-line {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 18px;
  padding: 8px 16px 0;
  font-size: 13px;
}
.ai-views {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 16px;
}
.ai-per-page {
  max-width: 110px;
}
.ai-narrow {
  max-width: 130px;
}
.ai-hint,
.ai-muted {
  color: rgba(0, 0, 0, 0.55);
  font-size: 12px;
}
/* Table chrome comes from the shared .fin-table layer (finance-compact.css,
   final-version doc 2026-10-05). */
.ai-table-wrap {
  --fin-table-max-h: calc(100vh - 300px);
  min-height: 280px;
}
.ai-group-row td {
  background: #e3f2fd;
  font-weight: 700;
  cursor: pointer;
}
.ai-total-row td {
  background: #e8f5e9;
  font-weight: 700;
}
.ai-detail-row td {
  background: #fafafa;
  white-space: normal;
}
.ai-detail-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 20px;
}
.ai-detail-item {
  min-width: 200px;
}
.ai-detail-label {
  color: rgba(0, 0, 0, 0.55);
  margin-inline-end: 6px;
}
.ai-detail-value {
  font-weight: 600;
}
.ai-row-abnormal td {
  background: #ffebee;
}
.ai-row-review td {
  background: #fff3e0;
}
.ai-row-closed td {
  color: rgba(0, 0, 0, 0.45);
}
.ai-abnormal {
  color: #c62828;
  font-size: 12px;
}
.ai-missing {
  color: #c62828;
  font-style: italic;
}
.ai-flag {
  display: inline-block;
  margin-inline-start: 6px;
  padding: 0 6px;
  border-radius: 8px;
  background: #eceff1;
  font-size: 11px;
}
.ai-status {
  font-weight: 600;
}
.ai-status-active {
  color: #2e7d32;
}
.ai-status-semi_active {
  color: #558b2f;
}
.ai-status-dormant {
  color: #ef6c00;
}
.ai-status-very_dormant {
  color: #c62828;
}
.ai-status-unused,
.ai-status-closed {
  color: rgba(0, 0, 0, 0.5);
}
.ai-action-correct_balance,
.ai-action-urgent_review {
  color: #c62828;
  font-weight: 600;
}
.ai-action-alert {
  color: #ef6c00;
  font-weight: 600;
}
.ai-action-close {
  color: #455a64;
  font-weight: 600;
}
.ai-side-debit {
  color: #1565c0;
}
.ai-side-credit {
  color: #2e7d32;
}
.ai-side-balanced {
  color: rgba(0, 0, 0, 0.55);
}
.ai-toggle-cell {
  width: 30px;
  padding: 0 2px;
}
.ai-toggle {
  margin: 0;
  height: 22px;
  width: 22px;
}
.ai-pager {
  display: flex;
  justify-content: center;
  padding: 8px;
}
.ai-notes {
  padding: 4px 16px 12px;
}
.ai-note {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.6);
  margin: 2px 0;
}
@media print {
  .fr-no-print {
    display: none !important;
  }
}
</style>
